import {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  keepPreviousData,
  useQuery,
} from "@tanstack/react-query";
import {
  api,
  type AssessmentRunStatus,
} from "../../lib/api";
import {
  fmtRange,
} from "../../lib/format";



export function RunHistoryPanel({ activeRunId }: { activeRunId: string | null }) {
  const PAGE_SIZE = 10;
  const [pages, setPages] = useState<number>(1);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Reuses the same cache key as the runner above so we don't double-fetch.
  const teamsQ = useQuery({
    queryKey: ["assessment-runs-assignable-teams"],
    queryFn: () => api.assessmentRunsAssignableTeams(),
    placeholderData: keepPreviousData,
  });
  const teamNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of teamsQ.data ?? []) m.set(t.id, t.name);
    return m;
  }, [teamsQ.data]);

  function scopeLabel(run: AssessmentRunStatus): string {
    const s = run.scope;
    if (!s) return "—";
    if (s.kind === "all_eligible") return "All eligible";
    if (s.kind === "by_team") {
      const name = s.team_id ? teamNameById.get(s.team_id) : null;
      if (name) return name;
      const shortId = s.team_id ? s.team_id.slice(0, 8) : "";
      return shortId ? `Unknown team (${shortId})` : "Unknown team";
    }
    return "—";
  }

  function windowLabel(run: AssessmentRunStatus): string {
    if (!run.range_start || !run.range_end) return "—";
    return fmtRange(run.range_start, run.range_end);
  }

  const historyQ = useQuery({
    queryKey: ["assessment-runs-history", pages],
    queryFn: () =>
      api.assessmentRunsList(PAGE_SIZE * pages, 0),
    refetchInterval: (q) => {
      const data = q.state.data as
        | { items?: AssessmentRunStatus[] }
        | undefined;
      const items = data?.items ?? [];
      const top = items[0];
      const activeOnTop =
        !!activeRunId &&
        top !== undefined &&
        top.run_id === activeRunId &&
        (top.status === "queued" || top.status === "running");
      const hasInFlight = items.some(
        (r) => r.status === "queued" || r.status === "running",
      );
      // Poll faster while anything is actually still running. Once every
      // run in the visible page has settled, back off — `activeRunId` from
      // the parent can linger after the run finishes, so we only treat
      // it as "in flight" when the matching row hasn't reached a terminal
      // state yet.
      if (hasInFlight || activeOnTop) return 2000;
      return 15000;
    },
    placeholderData: keepPreviousData,
  });

  const items = historyQ.data?.items ?? [];
  const hasMore = historyQ.data?.has_more ?? false;

  // When a fresh manual run is enqueued, kick a one-shot refetch so the
  // "Running…" row appears immediately instead of waiting for the next
  // background poll. After that, the in-flight detection in
  // refetchInterval keeps the panel on the 2s cadence on its own.
  useEffect(() => {
    if (activeRunId) {
      historyQ.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRunId]);

  function toggle(id: string) {
    setExpanded((m) => ({ ...m, [id]: !m[id] }));
  }

  function fmtRelative(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
    const abs = Math.abs(diffSec);
    if (abs < 60) return diffSec < 0 ? "in a moment" : "just now";
    if (abs < 3600) {
      const m = Math.round(abs / 60);
      return `${m}m ${diffSec < 0 ? "from now" : "ago"}`;
    }
    if (abs < 86400) {
      const h = Math.round(abs / 3600);
      return `${h}h ${diffSec < 0 ? "from now" : "ago"}`;
    }
    const days = Math.round(abs / 86400);
    if (days < 30) return `${days}d ${diffSec < 0 ? "from now" : "ago"}`;
    return d.toLocaleString();
  }

  function statusLabel(s: AssessmentRunStatus["status"]): {
    label: string;
    cls: string;
  } {
    switch (s) {
      case "queued":
        return { label: "Queued", cls: "bg-muted text-muted-foreground" };
      case "running":
        return {
          label: "Running…",
          cls: "bg-primary/15 text-primary border border-primary/30",
        };
      case "completed":
        return {
          label: "Succeeded",
          cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
        };
      case "partial":
        return {
          label: "Partial",
          cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
        };
      case "failed":
        return {
          label: "Failed",
          cls: "bg-destructive/15 text-destructive",
        };
      case "cancelled":
        return {
          label: "Cancelled",
          cls: "bg-muted text-muted-foreground border border-border",
        };
      default:
        return { label: s, cls: "bg-muted text-muted-foreground" };
    }
  }

  return (
    <div className="space-y-2 pt-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-foreground">Run history</h3>
        {historyQ.isFetching && (
          <span className="text-xs text-muted-foreground">Refreshing…</span>
        )}
      </div>

      {historyQ.isError && (
        <div className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Couldn't load run history.{" "}
          <button
            type="button"
            className="underline"
            onClick={() => historyQ.refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {!historyQ.isError && !historyQ.isLoading && items.length === 0 && (
        <div className="rounded border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
          No assessment runs yet. Trigger one above and it'll show up here.
        </div>
      )}

      {items.length > 0 && (
        <div className="rounded border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-3 py-2 w-6"></th>
                <th className="text-left font-medium px-3 py-2">Trigger</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
                <th className="text-left font-medium px-3 py-2">Finished</th>
                <th className="text-right font-medium px-3 py-2">Succeeded</th>
                <th className="text-right font-medium px-3 py-2">Failed</th>
                <th className="text-right font-medium px-3 py-2">Tokens</th>
                <th className="text-left font-medium px-3 py-2">Model</th>
                <th className="text-right font-medium px-3 py-2">Duration</th>
              </tr>
            </thead>
            <tbody>
              {items.map((run) => {
                const isOpen = !!expanded[run.run_id];
                const sl = statusLabel(run.status);
                const trigger = run.sync_window_id
                  ? "auto-chained"
                  : run.source_run_id
                    ? `manual · retry of ${run.source_run_id.slice(0, 8)}`
                    : "manual";
                const finishedRel =
                  run.status === "queued" || run.status === "running"
                    ? "—"
                    : fmtRelative(run.completed_at);
                const dur =
                  run.summary.duration_ms != null
                    ? `${(run.summary.duration_ms / 1000).toFixed(1)}s`
                    : "—";
                return (
                  <Fragment key={run.run_id}>
                    <tr
                      className="border-t border-border hover:bg-muted/30 cursor-pointer"
                      onClick={() => toggle(run.run_id)}
                    >
                      <td className="px-3 py-2 text-muted-foreground tabular-nums">
                        {isOpen ? "▾" : "▸"}
                      </td>
                      <td className="px-3 py-2 text-foreground">
                        <div>{trigger}</div>
                        <div
                          className="text-xs text-muted-foreground truncate max-w-[14rem]"
                          title={`${scopeLabel(run)} · ${windowLabel(run)}`}
                        >
                          {scopeLabel(run)}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded px-1.5 py-0.5 text-xs ${sl.cls}`}
                        >
                          {sl.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {finishedRel}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {run.summary.succeeded}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {run.summary.failed}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-xs text-muted-foreground">
                        {run.summary.input_tokens.toLocaleString()} /{" "}
                        {run.summary.output_tokens.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {run.summary.model ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {dur}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-border bg-muted/10">
                        <td colSpan={9} className="px-3 py-3">
                          <div className="space-y-1 text-sm">
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground">
                              <div>
                                Team{" "}
                                <span className="text-foreground">
                                  {scopeLabel(run)}
                                </span>
                              </div>
                              <div>
                                Window{" "}
                                <span className="text-foreground tabular-nums">
                                  {windowLabel(run)}
                                </span>
                              </div>
                              <div>
                                Succeeded{" "}
                                <span className="text-foreground tabular-nums">
                                  {run.summary.succeeded}
                                </span>
                              </div>
                              <div>
                                Failed{" "}
                                <span className="text-foreground tabular-nums">
                                  {run.summary.failed}
                                </span>
                              </div>
                              <div>
                                Tokens{" "}
                                <span className="text-foreground tabular-nums">
                                  {run.summary.input_tokens.toLocaleString()} in
                                  /{" "}
                                  {run.summary.output_tokens.toLocaleString()}{" "}
                                  out
                                </span>
                              </div>
                              <div>
                                Model{" "}
                                <span className="font-mono text-xs text-foreground">
                                  {run.summary.model ?? "—"}
                                </span>
                              </div>
                              <div>
                                Duration{" "}
                                <span className="text-foreground tabular-nums">
                                  {dur}
                                </span>
                              </div>
                              <div>
                                Trigger{" "}
                                <span className="text-foreground">
                                  {trigger}
                                </span>
                              </div>
                              <div>
                                Completed{" "}
                                <span className="text-foreground">
                                  {run.completed_at
                                    ? new Date(
                                        run.completed_at,
                                      ).toLocaleString()
                                    : "—"}
                                </span>
                              </div>
                              <div>
                                Started{" "}
                                <span className="text-foreground">
                                  {run.started_at
                                    ? new Date(run.started_at).toLocaleString()
                                    : "—"}
                                </span>
                              </div>
                            </div>
                            {run.summary.skipped > 0 && (
                              <div className="text-xs text-muted-foreground">
                                skipped — already assessed in window:{" "}
                                <span className="text-foreground tabular-nums">
                                  {run.summary.skipped}
                                </span>
                              </div>
                            )}
                            {run.progress &&
                              (run.status === "queued" ||
                                run.status === "running") && (
                                <div className="text-xs text-muted-foreground">
                                  Progress{" "}
                                  <span className="text-foreground tabular-nums">
                                    {run.progress.completed}
                                  </span>{" "}
                                  /{" "}
                                  <span className="text-foreground tabular-nums">
                                    {run.progress.total}
                                  </span>{" "}
                                  pairs ·{" "}
                                  <span className="text-foreground tabular-nums">
                                    {run.progress.failed}
                                  </span>{" "}
                                  failed
                                </div>
                              )}
                            {run.error && (
                              <div className="text-xs text-destructive">
                                {run.error}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              Underlying job:{" "}
                              <a
                                href={`/api/jobs/${encodeURIComponent(run.run_id)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="font-mono underline hover:text-foreground"
                                title="Open raw job record"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {run.run_id}
                              </a>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div>
          <button
            type="button"
            className="text-sm rounded border border-input px-3 py-1.5 hover:bg-muted/40"
            onClick={() => setPages((p) => p + 1)}
            disabled={historyQ.isFetching}
          >
            {historyQ.isFetching ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}

// Format a Date for an HTML `datetime-local` input — the browser
// rejects the ISO `Z` suffix, so we slice to `YYYY-MM-DDTHH:mm` in
// local time. Used by DeleteResultsCard to seed inputs from the

